/**
 * Report viewer — implements the wireframe at
 * `C:\workspace\lucid\reports-viewer-wireframe.pdf`.
 *
 * Layout (top → bottom):
 *   A. Top bar — breadcrumb-style nav (left) + actions (right)
 *   B. Hero   — icon chip + title + description (no buttons here)
 *   C. Results header bar — "summary · meta" (left) + filter pill (right)
 *   D. Table  — iframe directly under (C), no Card wrapper
 */

import { useMemo, useState } from "react";
import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import { ArrowLeft, ArrowRight, CalendarRange, Download, Loader2, Pencil, Play, Plus, RefreshCw, X } from "lucide-react";
import {
  Alert,
  Button,
  Card,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Input,
} from "@flint/ui";
import { trpc } from "../../../lib/trpc.ts";
import { useIsOwner } from "../../../lib/permissions.ts";
import { WorkspaceShell } from "../../../features/workspace/index.ts";
import { ReportFrame, iconForReport, type ParameterDef } from "../../../features/reports/index.ts";

export const Route = createFileRoute("/(workspace)/reports/$reportKey")({
  component: ReportViewerPage,
});

function ReportViewerPage() {
  const { reportKey } = useParams({ from: "/(workspace)/reports/$reportKey" });
  const definition = trpc.reports.getDefinition.useQuery({ key: reportKey });
  const isOwner = useIsOwner();

  const [filterValues, setFilterValues] = useState<Record<string, unknown>>({});

  const parameters: ParameterDef[] = useMemo(() => {
    const raw = definition.data?.parameters ?? [];
    return (raw as ParameterDef[]).map((p) => ({
      name: p.name,
      label: p.label,
      type: p.type,
      ...(p.required !== undefined ? { required: p.required } : {}),
      ...(p.defaultValue !== undefined ? { defaultValue: p.defaultValue } : {}),
      ...(p.options !== undefined ? { options: p.options } : {}),
    }));
  }, [definition.data?.parameters]);

  // Seed defaults from the definition once.
  useMemo(() => {
    if (parameters.length === 0) return;
    if (Object.keys(filterValues).length > 0) return;
    const seed: Record<string, unknown> = {};
    for (const p of parameters) {
      if (p.defaultValue !== undefined) seed[p.name] = p.defaultValue;
    }
    if (Object.keys(seed).length > 0) setFilterValues(seed);
  }, [parameters, filterValues]);

  const execute = trpc.reports.execute.useMutation();
  const exportCsv = trpc.reports.exportCsv.useMutation();
  const exportPdf = trpc.reports.exportPdf.useMutation();

  const hasRun = execute.data !== undefined || execute.isError;
  const runAt = execute.isSuccess
    ? new Date().toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : null;

  function runReport() {
    execute.mutate({ key: reportKey, parameterValues: filterValues as never });
  }

  async function downloadCsv() {
    const result = await exportCsv.mutateAsync({
      key: reportKey,
      parameterValues: filterValues as never,
    });
    const blob = new Blob([result.csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = result.fileName;
    a.click();
    URL.revokeObjectURL(url);
  }

  async function downloadPdf() {
    const result = await exportPdf.mutateAsync({
      key: reportKey,
      parameterValues: filterValues as never,
    });
    const binary = atob(result.pdfBase64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    const blob = new Blob([bytes], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = result.fileName;
    a.click();
    URL.revokeObjectURL(url);
  }

  const ReportIcon = definition.data
    ? iconForReport(reportKey, definition.data.category)
    : Play;

  // Build the "Summary" line for the results bar — e.g. "Active employees · 5 records".
  // Uses the active filter value as a one-word qualifier when present.
  function summaryTitle(): string {
    if (!definition.data) return "";
    const qualifier = pickQualifier(filterValues, parameters);
    const base =
      definition.data.category === "employee" ? "employees" : "records";
    const verb = qualifier ? `${qualifier} ${base}` : `All ${base}`;
    const count = execute.data?.rowCount;
    return count !== undefined ? `${verb} · ${count} records` : verb;
  }

  return (
    <WorkspaceShell breadcrumbs={[{ label: "Reports", to: "/reports" }]}>
      <div className="space-y-4 p-6 max-w-7xl">
        {/* A — Top bar : breadcrumb-style nav (left) + actions (right) */}
        <div className="flex items-center justify-between gap-4 flex-wrap pb-3 border-b border-line">
          <nav className="flex items-center gap-2 text-sm">
            <Link
              to="/reports"
              className="inline-flex items-center gap-1.5 text-muted hover:text-ink transition-colors"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              <span>Reports</span>
            </Link>
            <span className="text-muted/40">/</span>
            <span className="text-ink font-medium truncate max-w-[300px]">
              {definition.data?.name ?? reportKey}
            </span>
          </nav>

          <div className="flex items-center gap-2 shrink-0">
            <Button
              onClick={runReport}
              disabled={!definition.data || execute.isPending}
              intent="primary"
              size="sm"
            >
              {execute.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Play className="h-4 w-4" />
              )}
              Run
            </Button>
            {isOwner && definition.data ? (
              <Button intent="ghost" size="sm" asChild>
                <Link to="/reports/new" search={{ edit: reportKey }}>
                  <Pencil className="h-4 w-4" />
                  Edit
                </Link>
              </Button>
            ) : null}
            {hasRun ? (
              <>
                <Button
                  intent="ghost"
                  size="sm"
                  onClick={runReport}
                  disabled={execute.isPending}
                  aria-label="Refresh"
                >
                  <RefreshCw className="h-4 w-4" />
                </Button>
                <Button
                  intent="ghost"
                  size="sm"
                  onClick={downloadCsv}
                  disabled={exportCsv.isPending}
                >
                  {exportCsv.isPending ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Download className="h-3.5 w-3.5" />
                  )}
                  CSV
                </Button>
                <Button
                  intent="ghost"
                  size="sm"
                  onClick={downloadPdf}
                  disabled={exportPdf.isPending}
                >
                  {exportPdf.isPending ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Download className="h-3.5 w-3.5" />
                  )}
                  PDF
                </Button>
              </>
            ) : null}
          </div>
        </div>

        {/* B — Hero : icon + title + description */}
        <header className="flex items-start gap-3">
          <span
            aria-hidden
            className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-bg text-ink shrink-0"
          >
            <ReportIcon size={20} strokeWidth={2} />
          </span>
          <div className="min-w-0 flex-1">
            <h1 className="text-xl font-semibold tracking-tight">
              {definition.data?.name ?? "Loading…"}
            </h1>
            {definition.data?.description ? (
              <p className="text-sm text-muted mt-1 max-w-3xl">
                {definition.data.description}
              </p>
            ) : null}
          </div>
        </header>

        {/* Body */}
        {definition.isLoading ? (
          <ReportSkeleton />
        ) : definition.error ? (
          <Alert intent="bad">Couldn't load report: {definition.error.message}</Alert>
        ) : (
          <>
            {/* C — Results header bar */}
            <ResultsBar
              title={summaryTitle()}
              runAt={runAt}
              timingMs={execute.data?.timings.totalMs}
              resultHash={execute.data?.resultHash}
              parameters={parameters}
              values={filterValues}
              onChange={(name, value) =>
                setFilterValues((v) => ({ ...v, [name]: value }))
              }
            />

            {/* D — Data area */}
            {!hasRun ? (
              <IdleHint />
            ) : execute.isPending ? (
              <ReportSkeleton />
            ) : execute.isError ? (
              <Alert intent="bad">Report failed: {execute.error.message}</Alert>
            ) : execute.data ? (
              <div className="border border-line rounded-sm overflow-hidden">
                <ReportFrame html={execute.data.html} />
              </div>
            ) : null}
          </>
        )}
      </div>
    </WorkspaceShell>
  );
}

// ── Results header bar ────────────────────────────────────────────────

function ResultsBar({
  title,
  runAt,
  timingMs,
  resultHash,
  parameters,
  values,
  onChange,
}: {
  title: string;
  runAt: string | null;
  timingMs: number | undefined;
  resultHash: string | undefined;
  parameters: ParameterDef[];
  values: Record<string, unknown>;
  onChange: (name: string, value: unknown) => void;
}) {
  return (
    <div className="flex items-start justify-between gap-4 flex-wrap px-4 py-3 bg-bg border border-line rounded-sm">
      {/* Left : summary + meta */}
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-ink">
          {title || "Ready to run"}
        </div>
        {runAt ? (
          <div className="text-xs text-muted mt-1">
            Last run: {runAt}
            {timingMs !== undefined ? ` · ${timingMs} ms` : ""}
            {resultHash ? (
              <span>
                {" "}
                ·{" "}
                <code className="font-mono text-xs bg-paper px-1 py-0.5 rounded">
                  #{resultHash.slice(0, 8)}
                </code>
              </span>
            ) : null}
          </div>
        ) : (
          <div className="text-xs text-muted mt-1">
            Run the report to see results.
          </div>
        )}
      </div>

      {/* Right : filter widgets + Add filter */}
      <div className="flex flex-col items-end gap-1.5 shrink-0">
        <FilterRow parameters={parameters} values={values} onChange={onChange} />
        <button
          type="button"
          disabled
          className="inline-flex items-center gap-1 text-xs text-sky-700 cursor-not-allowed opacity-60"
          title="Multi-filter reports — V1.x"
        >
          <Plus className="h-3 w-3" />
          Add filter
        </button>
      </div>
    </div>
  );
}

/**
 * Render the right-hand filter widgets. Special-cases the
 * `dateFrom` + `dateTo` parameter pair into a single date-range
 * widget (a Calendar icon + From input + → + To input + clear),
 * which reads more naturally than two stacked date rows with the
 * long "Joined on or after / before" labels.
 */
function FilterRow({
  parameters,
  values,
  onChange,
}: {
  parameters: ParameterDef[];
  values: Record<string, unknown>;
  onChange: (name: string, value: unknown) => void;
}) {
  const from = parameters.find((p) => p.name === "dateFrom" && p.type === "date");
  const to = parameters.find((p) => p.name === "dateTo" && p.type === "date");
  const others = parameters.filter((p) => p !== from && p !== to);
  const isPair = !!from && !!to;

  return (
    <>
      {isPair ? (
        <DateRangeFilter
          fromName={from!.name}
          toName={to!.name}
          fromValue={typeof values[from!.name] === "string" ? (values[from!.name] as string) : ""}
          toValue={typeof values[to!.name] === "string" ? (values[to!.name] as string) : ""}
          onChange={onChange}
        />
      ) : null}
      {others.map((p) => (
        <CompactFilter
          key={p.name}
          def={p}
          value={values[p.name]}
          onChange={(v) => onChange(p.name, v)}
        />
      ))}
    </>
  );
}

function DateRangeFilter({
  fromName,
  toName,
  fromValue,
  toValue,
  onChange,
}: {
  fromName: string;
  toName: string;
  fromValue: string;
  toValue: string;
  onChange: (name: string, value: unknown) => void;
}) {
  const hasAny = !!fromValue || !!toValue;
  return (
    <div className="inline-flex items-center gap-2 h-8 pl-2.5 pr-1 rounded-sm border border-line bg-paper text-xs focus-within:border-sky-600 focus-within:ring-2 focus-within:ring-sky-100 transition-colors">
      <CalendarRange className="h-3.5 w-3.5 text-muted shrink-0" aria-hidden />
      <span className="text-muted whitespace-nowrap">Joined</span>
      <input
        type="date"
        value={fromValue}
        onChange={(e) => onChange(fromName, e.target.value || undefined)}
        aria-label="Joined on or after"
        className="h-6 w-[125px] bg-transparent border-0 outline-0 text-xs text-ink px-1 [color-scheme:light] cursor-pointer"
      />
      <ArrowRight className="h-3 w-3 text-muted/60 shrink-0" aria-hidden />
      <input
        type="date"
        value={toValue}
        onChange={(e) => onChange(toName, e.target.value || undefined)}
        aria-label="Joined on or before"
        className="h-6 w-[125px] bg-transparent border-0 outline-0 text-xs text-ink px-1 [color-scheme:light] cursor-pointer"
      />
      <button
        type="button"
        onClick={() => {
          onChange(fromName, undefined);
          onChange(toName, undefined);
        }}
        disabled={!hasAny}
        aria-label="Clear date range"
        className="inline-flex items-center justify-center h-6 w-6 rounded-sm text-muted hover:text-ink hover:bg-bg disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer transition-colors"
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  );
}

function CompactFilter({
  def,
  value,
  onChange,
}: {
  def: ParameterDef;
  value: unknown;
  onChange: (v: unknown) => void;
}) {
  if (def.type === "select" && def.options) {
    return (
      <div className="flex items-center gap-1.5 text-xs">
        <span className="text-muted">{def.label}:</span>
        <Select
          value={
            typeof value === "string"
              ? value
              : (def.defaultValue as string | undefined) ?? ""
          }
          onValueChange={(v) => onChange(v)}
        >
          <SelectTrigger className="h-7 px-2 text-xs min-w-[110px]">
            <SelectValue placeholder="—" />
          </SelectTrigger>
          <SelectContent>
            {def.options.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }
  if (def.type === "date") {
    return (
      <div className="flex items-center gap-1.5 text-xs">
        <span className="text-muted whitespace-nowrap">{def.label}:</span>
        <Input
          type="date"
          value={typeof value === "string" ? value : ""}
          onChange={(e) => onChange(e.target.value || undefined)}
          className="h-7 text-xs min-w-[140px]"
        />
      </div>
    );
  }
  return null;
}

// ── States ────────────────────────────────────────────────────────────

function IdleHint() {
  return (
    <Card className="p-10">
      <div className="flex flex-col items-center justify-center text-center max-w-md mx-auto">
        <div className="h-12 w-12 rounded-md bg-bg text-ink flex items-center justify-center mb-3">
          <Play size={24} strokeWidth={1.5} />
        </div>
        <h3 className="text-sm font-semibold">Ready to run</h3>
        <p className="text-xs text-muted mt-1.5">
          Adjust the filters above (if any) and click{" "}
          <strong className="text-ink">Run</strong> to generate results.
        </p>
      </div>
    </Card>
  );
}

function ReportSkeleton() {
  return (
    <Card className="overflow-hidden">
      <div className="p-3 border-b bg-bg/30">
        <div className="flex gap-3">
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-4 w-28" />
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-4 w-20" />
        </div>
      </div>
      <div className="p-3 space-y-2.5">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="flex gap-3">
            <Skeleton className="h-4 w-16" />
            <Skeleton className="h-4 w-28" />
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-4 w-20" />
          </div>
        ))}
      </div>
    </Card>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────

/**
 * Pick a one-word qualifier from the first set filter value for the
 * summary line ("Active employees · …"). Returns "" when nothing useful.
 */
function pickQualifier(
  values: Record<string, unknown>,
  parameters: ParameterDef[],
): string {
  for (const p of parameters) {
    const v = values[p.name];
    if (typeof v !== "string" || !v || v === "all") continue;
    const opt = p.options?.find((o) => o.value === v);
    return (opt?.label ?? v).replace(/^./, (c) => c.toUpperCase());
  }
  return "";
}
